import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import Message from './Message';
import debounce from 'lodash/debounce';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, limit } from 'firebase/firestore';
import '../styles/Chat.css';

const MESSAGES_PER_PAGE = 50;

const Chat = () => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const contact = location.state?.contact;

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (!contact) {
      navigate('/contacts');
      return;
    }

    // Validate contact data
    console.log('Contact data:', contact);
    if (!contact?.chatRoomId) {
      console.error('Invalid contact data:', contact);
      setError('Invalid chat room. Please try again from contacts.');
      navigate('/contacts');
    }

    // Subscribe to messages for this chat room
    if (contact.chatRoomId) {
      console.log('Subscribing to messages for chat room:', contact.chatRoomId);
      const messagesRef = collection(db, 'messages');
      
      // First try to get messages without ordering to show something while index is being created
      try {
        const q = query(
          messagesRef,
          where('chatRoomId', '==', contact.chatRoomId),
          limit(50)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
          console.log('Received message snapshot:', snapshot.docs.length, 'messages');
          if (snapshot.empty) {
            console.log('No messages found in this chat room');
            setMessages([]);
            return;
          }

          const messagesData = snapshot.docs
            .map(doc => {
              const data = {
                id: doc.id,
                ...doc.data()
              };
              console.log('Message data:', data);
              return data;
            })
            .sort((a, b) => {
              // Sort by timestamp if available, otherwise keep original order
              if (!a.timestamp || !b.timestamp) return 0;
              return a.timestamp.seconds - b.timestamp.seconds;
            });
          
          console.log('Setting messages:', messagesData);
          setMessages(messagesData);
          scrollToBottom();
        }, (error) => {
          console.error('Error in message subscription:', error);
          if (error.message.includes('requires an index')) {
            setError('Chat room is being set up. Please wait a moment and refresh.');
          } else {
            setError('Failed to load messages');
          }
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error setting up message subscription:', error);
        setError('Failed to connect to chat room');
      }
    } else {
      console.error('No chatRoomId provided');
      setError('Chat room not found');
    }
  }, [currentUser, contact, navigate]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleScroll = useCallback(
    debounce((e) => {
      const element = e.target;
      if (element.scrollTop === 0 && hasMore && !loading) {
        setLoading(true);
        try {
          // Load more messages logic here if needed
          setLoading(false);
        } catch (error) {
          console.error('Error loading more messages:', error);
          setError('Failed to load more messages');
          setLoading(false);
        }
      }
    }, 200),
    [hasMore, loading]
  );

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !contact?.chatRoomId) {
      console.error('Missing required data:', { message: !!message.trim(), chatRoomId: contact?.chatRoomId });
      return;
    }

    try {
      setError(''); // Clear any previous errors
      console.log('Sending message to chat room:', contact.chatRoomId);
      const messageData = {
        text: message.trim(),
        sender: currentUser.email,
        receiver: contact.email,
        chatRoomId: contact.chatRoomId,
        timestamp: serverTimestamp(),
        read: false,
        delivered: false
      };

      console.log('Message data to send:', messageData);
      const docRef = await addDoc(collection(db, 'messages'), messageData);
      console.log('Message sent successfully, doc ID:', docRef.id);
      setMessage('');
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="header-left">
          <button onClick={() => navigate('/contacts')} className="back-button">
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="contact-info">
            <h2>{contact?.displayName || 'Chat'}</h2>
          </div>
        </div>
      </div>

      <div className="messages-container" ref={messagesContainerRef}>
        {loading && <div className="loading">Loading messages...</div>}
        {error && <div className="error-message">{error}</div>}
        {messages.map((msg) => (
          <Message
            key={msg.id}
            message={{
              ...msg,
              userId: msg.sender === currentUser.email ? currentUser.uid : null
            }}
            currentUser={currentUser}
            isOwn={msg.sender === currentUser.email}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="message-input-container">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type a message..."
          className="message-input"
        />
        <button type="submit" className="send-button" disabled={!message.trim()}>
          <i className="fas fa-paper-plane"></i>
        </button>
      </form>
    </div>
  );
};

export default Chat;
